import { useState } from 'react'
import {
  useDashboardOverview,
  useUserAnalytics,
  useUsageAnalytics,
  type AnalyticsPeriod,
} from '@/hooks/useAnalytics'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { QueryError } from '@/components/QueryError'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatNumber } from '@/lib/utils'
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import {
  Users,
  UserCheck,
  UserPlus,
  TrendingUp,
  Activity,
  FileText,
  DollarSign,
} from 'lucide-react'

const COLORS = ['#7BA68A', '#D4942A', '#5B8FA8', '#C4644A', '#8B7EC8', '#6B9B7B']

const PERIOD_LABELS: Record<AnalyticsPeriod, string> = {
  '7d': '7 dias',
  '30d': '30 dias',
  '90d': '90 dias',
  '6m': '6 meses',
  '1y': '1 anio',
}

export const AnalyticsPage = () => {
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d')

  const {
    data: overview,
    isLoading: overviewLoading,
    isError: overviewError,
    isFetching: overviewFetching,
    refetch: refetchOverview,
  } = useDashboardOverview(period)
  const {
    data: userAnalytics,
    isLoading: usersLoading,
    isError: usersError,
    isFetching: usersFetching,
    refetch: refetchUsers,
  } = useUserAnalytics(period)
  const {
    data: usageAnalytics,
    isLoading: usageLoading,
    isError: usageError,
    isFetching: usageFetching,
    refetch: refetchUsage,
  } = useUsageAnalytics(period)

  const hasError = overviewError || usersError || usageError
  const refetchAll = () => {
    refetchOverview()
    refetchUsers()
    refetchUsage()
  }

  const periodLabel = PERIOD_LABELS[period]

  const statCards = overview
    ? [
        { label: 'Total Usuarios', value: formatNumber(overview.total_users), icon: Users },
        {
          label: `Activos (${periodLabel})`,
          value: formatNumber(overview.active_users),
          icon: UserCheck,
        },
        {
          label: `Nuevos (${periodLabel})`,
          value: formatNumber(overview.new_users),
          icon: UserPlus,
        },
        {
          label: `Ingresos (${periodLabel})`,
          value: `$${overview.total_revenue.toLocaleString()}`,
          icon: DollarSign,
        },
        {
          label: `Docs (${periodLabel})`,
          value: formatNumber(overview.docs_consumed),
          icon: FileText,
        },
        {
          label: 'Churn Rate',
          value: `${overview.churn_rate.toFixed(1)}%`,
          icon: TrendingUp,
          destructive: overview.churn_rate > 5,
        },
      ]
    : []

  const planData = userAnalytics
    ? Object.entries(userAnalytics.by_plan).map(([name, count]) => ({ name, value: count }))
    : []

  const platformData = usageAnalytics?.by_platform ?? []

  const formatDateLabel = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('es', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Analytics</h1>
        <Tabs value={period} onValueChange={(v) => setPeriod(v as AnalyticsPeriod)}>
          <TabsList>
            <TabsTrigger value="7d">7d</TabsTrigger>
            <TabsTrigger value="30d">30d</TabsTrigger>
            <TabsTrigger value="90d">90d</TabsTrigger>
            <TabsTrigger value="6m">6m</TabsTrigger>
            <TabsTrigger value="1y">1a</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {hasError && (
        <QueryError
          message="No se pudieron cargar algunas métricas de analytics."
          onRetry={refetchAll}
          isRetrying={overviewFetching || usersFetching || usageFetching}
        />
      )}

      {/* Stat Cards */}
      {overviewLoading ? (
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
          {statCards.map((s) => {
            const Icon = s.icon
            return (
              <Card key={s.label}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">{s.label}</p>
                      <p className={`text-xl font-bold ${s.destructive ? 'text-destructive' : ''}`}>
                        {s.value}
                      </p>
                    </div>
                    <Icon className="h-6 w-6 text-muted-foreground/40" />
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Trend Charts Row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* New Users Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Nuevos Usuarios</CardTitle>
          </CardHeader>
          <CardContent>
            {overviewLoading ? (
              <Skeleton className="h-48" />
            ) : overview?.new_users_trend?.length ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={overview.new_users_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={formatDateLabel} tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip
                    labelFormatter={formatDateLabel}
                    formatter={(v: number) => [v, 'Registros']}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#7BA68A"
                    fill="#7BA68A"
                    fillOpacity={0.15}
                    strokeWidth={2}
                    name="Registros"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="flex h-48 items-center justify-center text-sm text-muted-foreground">
                Sin datos en este periodo
              </p>
            )}
          </CardContent>
        </Card>

        {/* Docs Consumed Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Documentos Consumidos</CardTitle>
          </CardHeader>
          <CardContent>
            {usageLoading ? (
              <Skeleton className="h-48" />
            ) : usageAnalytics?.docs_trend?.length ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={usageAnalytics.docs_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={formatDateLabel} tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip
                    labelFormatter={formatDateLabel}
                    formatter={(v: number) => [v, 'Docs']}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#5B8FA8"
                    fill="#5B8FA8"
                    fillOpacity={0.15}
                    strokeWidth={2}
                    name="Docs"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="flex h-48 items-center justify-center text-sm text-muted-foreground">
                Sin datos en este periodo
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* User Engagement (DAU/WAU/MAU - fixed metrics) */}
      {userAnalytics && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-muted-foreground">DAU</p>
              <p className="text-3xl font-bold">{formatNumber(userAnalytics.active_daily)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-muted-foreground">WAU</p>
              <p className="text-3xl font-bold">{formatNumber(userAnalytics.active_weekly)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-muted-foreground">MAU</p>
              <p className="text-3xl font-bold">{formatNumber(userAnalytics.active_monthly)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-muted-foreground">Crecimiento</p>
              <p className="text-3xl font-bold">
                <span
                  className={userAnalytics.growth_rate >= 0 ? 'text-green-600' : 'text-red-500'}
                >
                  {userAnalytics.growth_rate >= 0 ? '+' : ''}
                  {userAnalytics.growth_rate.toFixed(1)}%
                </span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                <Activity className="mr-1 inline h-3 w-3" />
                Mes actual vs anterior
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Detail Tabs */}
      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users">Usuarios por Plan</TabsTrigger>
          <TabsTrigger value="usage">Uso por Plataforma</TabsTrigger>
          <TabsTrigger value="registrations">Registros Diarios</TabsTrigger>
          <TabsTrigger value="top">Top Usuarios</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Distribucion por Plan</CardTitle>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <Skeleton className="h-64" />
              ) : planData.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={planData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {planData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-muted-foreground">Sin datos</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="usage" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Documentos por Plataforma</CardTitle>
            </CardHeader>
            <CardContent>
              {usageLoading ? (
                <Skeleton className="h-64" />
              ) : platformData.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={platformData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="platform" />
                    <YAxis />
                    <Tooltip />
                    <Bar
                      dataKey="documents"
                      fill="#7BA68A"
                      name="Documentos"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-muted-foreground">Sin datos de plataforma</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="registrations" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Registros Diarios ({periodLabel})</CardTitle>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <Skeleton className="h-64" />
              ) : userAnalytics?.registrations_trend?.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={userAnalytics.registrations_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={formatDateLabel} />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      labelFormatter={formatDateLabel}
                      formatter={(v: number) => [v, 'Registros']}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#D4942A"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      name="Registros"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-muted-foreground">Sin registros en este periodo</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="top" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Top Usuarios por Uso</CardTitle>
            </CardHeader>
            <CardContent>
              {usageLoading ? (
                <Skeleton className="h-64" />
              ) : usageAnalytics?.top_users?.length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead className="text-right">Docs Usados</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usageAnalytics.top_users.map((u, i) => (
                      <TableRow key={u.user_id}>
                        <TableCell className="font-medium">{i + 1}</TableCell>
                        <TableCell>{u.name}</TableCell>
                        <TableCell className="text-muted-foreground">{u.email}</TableCell>
                        <TableCell className="capitalize">{u.plan_name}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatNumber(u.docs_used)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">Sin datos</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
